import {
  removeTrapFocus,
  trapFocus,
  setInertOnSiblings,
  removeInertFromSiblings,
  setBackgroundAriaHidden,
} from '@/lib/a11y'
import DetailsModal from './details-modal'

/**
 * HeaderDrawer class represents a custom web component that extends DetailsModal.
 * It provides methods for opening and closing a header drawer with accessibility considerations.
 */
class HeaderDrawer extends DetailsModal {
  /**
   * Opens the header drawer.
   * @param {Event} event - The event that triggered the opening action.
   */
  open(event) {
    setTimeout(() => {
      this.detailsContainer.classList.add('menu-opening')
    })
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this)
    event.target.closest('details').setAttribute('open', true)

    // Set accessibility attributes
    this.detailsContainer.setAttribute('aria-modal', 'true')
    this.detailsContainer.setAttribute('role', 'dialog')

    // Set aria-hidden on main content (exclude header since drawer is inside it)
    setBackgroundAriaHidden(true, this, { includeHeader: false })

    document.body.addEventListener('click', this.onBodyClickEvent)
    document.body.classList.add('overflow-hidden', 'lg:overflow-auto')

    // Set inert on background content to prevent focus escape
    setInertOnSiblings(this.detailsContainer)

    // Update aria-expanded on summary
    this.summaryToggle.setAttribute('aria-expanded', 'true')

    trapFocus(this.detailsContainer.querySelector('[tabindex="-1"]'))

    // Remove lazy loading on images
    const images = this.detailsContainer.querySelectorAll('img')

    images.forEach((image) => {
      image.removeAttribute('loading')
    })
  }

  /**
   * Closes the header drawer.
   * @param {boolean} focusToggle - Whether to toggle focus back after closing.
   */
  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null)
    removeInertFromSiblings()
    document.body.removeEventListener('click', this.onBodyClickEvent)
    this.detailsContainer.classList.remove('menu-opening')
    document.body.classList.remove('overflow-hidden', 'lg:overflow-auto')

    // Remove accessibility attributes
    this.detailsContainer.removeAttribute('aria-modal')
    this.detailsContainer.removeAttribute('role')

    // Remove aria-hidden from background content
    setBackgroundAriaHidden(false, this, { includeHeader: false })

    // Update aria-expanded on summary
    this.summaryToggle.setAttribute('aria-expanded', 'false')

    this.closeAnimation()
  }

  /**
   * Callback triggered when the component is connected to the DOM.
   * Adds a CSS class to indicate hydration status and applies menu-opening class if 'open' attribute exists.
   */
  connectedCallback() {
    this.classList.add('is-hydrated')

    if (this.detailsContainer.hasAttribute('open')) {
      this.detailsContainer.classList.add('menu-opening')
    }
  }

  /**
   * Initiates the animation for closing the header drawer.
   */
  closeAnimation() {
    let animationStart

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time
      }

      this.detailsContainer.removeAttribute('open')
    }

    window.requestAnimationFrame(handleAnimation)
  }
}

// Define the 'header-drawer' custom element using the HeaderDrawer class.
window.customElements.define('header-drawer', HeaderDrawer)
